// Slice B2.4 of docs/planning/activity_dock_workflow_facilitation.md:
// the "should the dock peek right now?" decision moves out of a single
// rollup `tone` (B2.1) into a section-aware predicate over the role-
// filtered registry. Each section can declare:
//
//   - `modeRelevance: PrimaryMode[]` — modes where its signal is worth
//     interrupting the user. Omitted ⇒ relevant in every mode.
//   - `hasSignal(ctx)` — section-owned predicate over a narrow context
//     bag (`unsentCount`, `adsTone`, `hasRole`). Omitted ⇒ section is
//     informational and never drives a peek.
//
// The dock asks `dockHasPeekWorthySignal(...)` on every mode-change
// candidate; if any visible, mode-relevant section reports a signal,
// the peek lifecycle in `useActivityDockModePeek` fires (subject to
// the assist-level + dedup gates that have always governed it).
//
// `buildDockPeekContext` is the single place we derive the predicate
// inputs from live dock state — keeping it next to the consumer so
// the tab badge (`deriveActivityDockStatus`) and the peek decision
// stay readable from one file.
//
// Both helpers are pure so they can be unit-tested without React or
// any of the dock's surrounding context providers.

import type { AdsActivityResponse } from "@/lib/adsAssistantClientEngagement";
import type { KpiTone } from "@/components/common/kpi/kpiTypes";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { Lead } from "@/types/lead";
import { resolveAdsHeaderActivityPill } from "@/components/ads-assistant/resolveAdsHeaderActivityPill";
import type {
  DockSectionDefinition,
  DockSectionPeekContext,
  HasRoleFn,
} from "./sectionRegistry";

/**
 * Result of the section-aware peek decision. `reasonSectionId` is the
 * id of the first mode-relevant section whose `hasSignal` returned
 * `true`, or `null` when no section signalled. The dock hook uses it
 * as the gate for the auto-open lifecycle; the telemetry layer (B2.4-B)
 * uses it as the "which section earned the user's attention?" reason
 * code emitted on each peek-fire event.
 */
export type DockPeekDecision = {
  shouldPeek: boolean;
  reasonSectionId: string | null;
};

/**
 * Pure decision: returns `{ shouldPeek, reasonSectionId }` for the
 * supplied sections and current mode.
 *
 * - `currentMode == null` ⇒ `{ shouldPeek: false, reasonSectionId: null }`
 *   (still bootstrapping; the dock hook already no-ops on this case
 *   but the helper is defensive).
 * - Sections without `hasSignal` are ignored (informational sections).
 * - Sections without `modeRelevance` are treated as "relevant in every
 *   mode" for back-compat with pre-B2.4 sections.
 * - Iteration order matches the registry's intentional render order;
 *   we short-circuit on the first match so the helper is O(N) in
 *   the section count and the `reasonSectionId` is stable / explainable
 *   (= "the first section that earned the peek").
 *
 * The `reasonSectionId` projection is the load-bearing change in slice
 * B2.4-B: turning "did a peek fire?" into "*why* did a peek fire?" so
 * the dock can emit a structured telemetry event identifying which
 * section's signal earned the user's attention — feeding future
 * deadweight analysis (sections that peek but get no follow-on
 * interaction). The boolean `shouldPeek` mirror is kept on the result
 * shape so call sites that don't care about the reason stay a one-line
 * destructure.
 */
export function evaluateDockPeek(input: {
  sections: readonly DockSectionDefinition[];
  currentMode: PrimaryMode | null | undefined;
  peekContext: DockSectionPeekContext;
}): DockPeekDecision {
  const { sections, currentMode, peekContext } = input;
  if (currentMode == null) return { shouldPeek: false, reasonSectionId: null };
  for (const section of sections) {
    if (!section.hasSignal) continue;
    if (section.modeRelevance && !section.modeRelevance.includes(currentMode)) {
      continue;
    }
    if (section.hasSignal(peekContext)) {
      return { shouldPeek: true, reasonSectionId: section.id };
    }
  }
  return { shouldPeek: false, reasonSectionId: null };
}

/**
 * Derive a fresh `DockSectionPeekContext` from the same data the dock
 * already has at hand. Centralizing the derivation here keeps every
 * `hasSignal` predicate looking at identical-shaped inputs and means
 * the tab-badge derivation (`activityDockStatus.ts`) and the peek
 * derivation read from one canonical pipeline.
 *
 * `unsentCount` mirrors `LeadsModeBanners`'s retired badge filter
 * (`!l.sent && !l.isTest`) — same as the count used to drive the
 * dock-tab badge. `adsTone` is the resolved ads-coaching pill tone
 * (or `null` when the pill isn't visible at all); section predicates
 * are responsible for any further role / threshold gating on top.
 */
export function buildDockPeekContext(input: {
  leads: readonly Lead[];
  adsActivity: AdsActivityResponse | null | undefined;
  hasRole: HasRoleFn;
  authStatus: string;
  kbGapOpenCount?: number | null;
  aiPromptingOpenCount?: number | null;
  pendingMemberApprovalTotal?: number | null;
  staleWacCount?: number | null;
  plantRequestReviewCount?: number | null;
  openQcCount?: number | null;
}): DockSectionPeekContext {
  const {
    leads,
    adsActivity,
    hasRole,
    authStatus,
    kbGapOpenCount = null,
    aiPromptingOpenCount = null,
    pendingMemberApprovalTotal = null,
    staleWacCount = null,
    plantRequestReviewCount = null,
    openQcCount = null,
  } = input;

  const unsentCount = leads.reduce(
    (n, l) => (!l.sent && !l.isTest ? n + 1 : n),
    0,
  );

  const pill = adsActivity
    ? resolveAdsHeaderActivityPill(adsActivity, {
        status: authStatus,
        hasRole,
      })
    : null;
  const adsTone: KpiTone | null = pill?.visible ? pill.tone : null;

  return {
    hasRole,
    unsentCount,
    adsTone,
    kbGapOpenCount,
    aiPromptingOpenCount,
    pendingMemberApprovalTotal,
    staleWacCount,
    plantRequestReviewCount,
    openQcCount,
  };
}
